import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Stepper, Step as MuiStep, StepLabel, StepContent } from '@mui/material';
import { makeStyles } from '@mui/styles';
import DialogPlantWorkflow from '@/components/DialogPlantWorkflow/DialogPlantWorkflow';
import DialogStatus from '@/components/DialogStatus/DialogStatus';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import TooltipComponent from '@/components/UI/Tooltip/TooltipComponent';
import UniversalChip from '@/components/UI/UniversalChip/UniversalChip';
import { statusCampaign, chipTypes, statusSubCampaign, actionTakenSubCampaign, WORKFLOW_TYPE } from '@/helpers/constants';
import useToastContext from '@/hooks/useToastContext';
import { getIsVANCR } from '@/redux/Campaign/campaign.reducer';
import campaignServices from '@/services/campaignServices';
import { CustomStep, PlanStatus, Step, Workflow, CampaignInfo } from '@/types';
import SubCRMiniWF from './SubCRMiniWF';
import { getStautsIconClassName } from './WorkFlow.utils';
import styles from './StepsComponent.module.scss';

const useStyles = makeStyles(() => ({
  stepper: { padding: '24px' },
  rootApproved: {
    borderColor: 'green',
    border: '3px solid',
  },
  rootChangeRequired: {
    borderColor: 'orange',
    border: '3px solid',
  },
}));

const getIPVDataByVersion = (step: Step, workflow: Workflow) => {
  if ((workflow?.version === parseInt(step.version, 10)) || !step.version) {
    return { plantSumLabel: workflow?.plantSumLabel, plantCounter: workflow?.plantCounter, plantStatuses: workflow?.plantStatuses };
  }

  return workflow?.historicalPlantStatuses?.find((hp) => hp.plantStatuses[0].campaignVersion === parseInt(step.version, 10)) || {};
};

const getCompletedTotalPlantsByLabel = (plantSumLabel: string) => {
  const [completed, total] = plantSumLabel?.split('/') ?? [0, 0];

  return { completed, total };
};

const processStatuses = (wfSstatuses, workflow) => wfSstatuses
  ?.map((st) => ({ ...st, ipvData: getIPVDataByVersion(st, workflow) }));

type StepComponent = {
  id: string;
  collapsed: boolean;
  nonLinear: boolean;
  workflow: Workflow;
  orientation: 'vertical' | 'horizontal';
  headerLanguage: string;
  campaignInfo: CampaignInfo;
  isSubCR: boolean;
  isVANCR: boolean;
  subStatus?: PlanStatus[] | null;
};

const StepsComponent: React.FC<StepComponent> = (props) => {
  const { id, collapsed, nonLinear, workflow, orientation, headerLanguage, campaignInfo, isSubCR, isVANCR, subStatus } = props;

  const [open, setOpen] = useState(false);
  const [stepStatus, setStepStatus] = useState<Step | null>(null);
  const [plantSelected, setPlantSelected] = useState<PlanStatus | null>(null);
  const [subCRClicked, setSubCRClicked] = useState<string>('');
  const { t } = useTranslation();
  const { addToast } = useToastContext();
  const [expandedView, setExpandedView] = useState(false);
  const [displayStatuses, setDisplayStatuses] = useState<CustomStep[]>(processStatuses(workflow?.statuses, workflow));
  let oldVersion = 1;
  const classes = useStyles();

  const isPlantValidation = (status) => [statusCampaign.IN_PLANT_VALIDATION, statusCampaign.IN_VAN_PLANT_VALIDATION].includes(status);

  useEffect(() => {
    if (workflow?.statusUserHasToSee && workflow?.statusUserHasToSee !== null) {
      setStepStatus(workflow?.statusUserHasToSee);
      setOpen(true);
    }
  }, [workflow]);

  const getStatusIconForPlant = (plant: PlanStatus) => {
    const { statusCode, actionTaken } = plant;
    let className = '';

    if (statusCode === statusSubCampaign.OPEN) {
      className = 'di icon-sanduhr'; // sandclock, without color / not asigned/open
    } else if (
      [statusSubCampaign.IN_PLANT_COORDINATION, statusSubCampaign.IN_VAN_PLANT_COORDINATION, statusCode === statusSubCampaign.IN_PLANT_PREPARATION]
        .includes(statusCode)) {
      className = 'di icon-sanduhr Orange'; // sandclock, orange / in plant coordination
    } else if (statusCode === statusSubCampaign.BLOCKED_BY_CHANGE_REQUEST) {
      className = 'di icon-stop-einspruch'; // hand, without color / blocked by change request
    } else if (statusCode === statusSubCampaign.IN_ANALYSIS && actionTaken !== actionTakenSubCampaign.REQUESTED_FOR_CHANGES) {
      className = 'di icon-datei-bearbeiten Grey'; // pencil, grey / in analysis
    } else if ([statusSubCampaign.IN_E3_APPROVAL, statusSubCampaign.IN_E4_APPROVAL].includes(statusCode)) {
      className = 'di icon-richterhammer-2 Blue'; // hammer, blue / in approval
    } else if (statusCode === statusSubCampaign.ACCEPTED && [actionTakenSubCampaign.APPROVED, ''].includes(actionTaken)) {
      className = 'di icon-kreis-check Success'; // check, green / accepted
    } else if (statusCode === statusSubCampaign.DENIED && actionTaken === actionTakenSubCampaign.REJECTED) {
      className = 'di icon-kreis-loeschen NonSuccess'; // X, red / denied
    } else if (([statusSubCampaign.IN_ANALYSIS, statusSubCampaign.REQUESTED_FOR_CHANGES].includes(statusCode)
      && actionTaken === actionTakenSubCampaign.REQUESTED_FOR_CHANGES)
    || (statusCode === statusSubCampaign.ACCEPTED && actionTaken === actionTakenSubCampaign.REQUESTED_FOR_CHANGES)) {
      className = 'di icon-kreis-entscheid-gelb Orange'; // Y, orange / change requested
    } else if (statusCode === statusSubCampaign.PLANT_NOT_AFFECTED) {
      className = 'di icon-kreis-loeschen Grey'; // X, grey / plant not affected
    }

    // PREPARATION COORDINATION
    return <i className={className} />;
  };

  const stepHandleClick = (step: Step) => {
    setStepStatus(step);
    setOpen(true);
  };

  const handlePlantClick = (subCRId: string, plant: PlanStatus) => {
    const { statusCode, actionTaken } = plant;

    if (statusCode === statusSubCampaign.ACCEPTED && actionTaken === '') { // Not ready plant
      addToast(t('workflow.notReadyPlantSelected'), 'Warning');
    } else {
      setSubCRClicked(subCRId);
      setPlantSelected(plant);
    }
  };

  const closeDialogStatus = () => {
    if (workflow?.statusUserHasToSee && workflow?.statusUserHasToSee !== null) {
      setOpen(true);
      campaignServices.closeWorkflowDialog(id, workflow?.statusUserHasToSee?.statusCode, isSubCR)
        .then(() => {
          setOpen(false);
          setStepStatus(null);
        }).catch((error) => addToast(error.message, 'ERROR'));
    } else {
      setOpen(false);
      setStepStatus(null);
    }
  };

  const closePlantDialog = () => {
    setPlantSelected(null);
  };

  const handleExpandedView = () => {
    setExpandedView((prev) => (!prev));
    setDisplayStatuses([]);
  };

  const removeRepeatedItem = (statusList: Step[]) => {
    if (statusList && [statusCampaign.IN_CREATION, statusCampaign.IN_PLANT_VALIDATION, statusCampaign.IN_VAN_PLANT_VALIDATION]
      .includes(campaignInfo?.status) && campaignInfo.version > 1) {
      return statusList?.slice(1);
    }

    return statusList;
  };

  useEffect(() => {
    setDisplayStatuses([]);
    oldVersion = 1;
    const filterStatuses = (expandedView && workflow?.historicalStatuses?.length > 0)
      ? [...workflow.historicalStatuses, ...removeRepeatedItem(workflow?.statuses)]
      : workflow?.statuses;

    setDisplayStatuses(processStatuses(expandedView ? filterStatuses : workflow?.statuses, workflow));
  }, [workflow, expandedView, collapsed]);

  const checkVersionChip = (stepVersion) => ((Number(stepVersion) < oldVersion) ? `V${stepVersion} -` : '');

  const formatCounter = (num: string) => {
    const parts = num.split('-');

    return parts[parts.length - 1];
  };

  const generatePlantName = ({ wkzNumber, plantName, subCampaignId }) => `W${wkzNumber} - ${plantName} - ${formatCounter(subCampaignId)}`;

  const getCRorSubCRLabelStatus = (status) => t([`campaign.${status}`, `subCampaign.${status}`]);

  const getActionTakenLabel = (actionTaken, statusCode) => {
    if (isSubCR && actionTaken === actionTakenSubCampaign.SENT_TO_APPROVAL && statusCode !== statusSubCampaign.IN_PLANT_VALIDATION) {
      return t('action.IN_PLANT_APPROVAL');
    }

    if (isSubCR && actionTaken === actionTakenSubCampaign.CREATED) {
      return t('action.OPEN');
    }

    return t(`action.${actionTaken}`);
  };

  const lastVersionIndex = useRef(0);

  const getIndexByVersion = (element, array) => {
    if (element.version === '') {
      lastVersionIndex.current += 1;

      return lastVersionIndex.current;
    }

    const index = array.filter((el) => el.version === element.version).indexOf(element);
    lastVersionIndex.current = index;

    return index;
  };

  const getStatusVersion = (step) => {
    if (isSubCR) {
      return displayStatuses.find((item) => [statusCampaign.IN_PLANT_VALIDATION, statusCampaign.IN_VAN_PLANT_VALIDATION].includes(item.statusCode)
        && item.version === step.version)!
        .subStatuses![0].version;
    }

    return step.version;
  };

  const checkVersions = (step) => {
    if (Number(step.version) > oldVersion) {
      oldVersion = Number(step.version);

      const stepVersion = getStatusVersion(step);

      return stepVersion !== '' && stepVersion !== '1';
    }

    return false;
  };

  const checkSubCrStatus = (plant: PlanStatus): string => {
    let chipStyle = '';
    const ACTIONS_COLOR_CODES = {
      [actionTakenSubCampaign.APPROVED]: classes.rootApproved,
      [actionTakenSubCampaign.REQUESTED_FOR_CHANGES]: classes.rootChangeRequired,
    };

    const subCampaignFound = subStatus?.find((subStatusItem) => subStatusItem.subCampaignId === plant?.subCampaignId);

    if (!subCampaignFound) {
      return chipStyle;
    }

    if (subCampaignFound.workflowDetails?.workflowType === WORKFLOW_TYPE.NOT_READY) {
      chipStyle = classes.rootApproved;

      return chipStyle;
    }

    const inCheckingStatus = subCampaignFound.workflowDetails?.statuses?.find((status) => status?.name === statusSubCampaign.IN_CHECKING);

    if (!inCheckingStatus) {
      return chipStyle;
    }

    chipStyle = ACTIONS_COLOR_CODES?.[inCheckingStatus.actionTaken] ?? '';

    return chipStyle;
  };

  return (
    <>
      <div className={styles.ExpandedButton}>
        <SimpleButton
          id="expanded-view-button"
          color="primary"
          buttonText={t(`workflow.${expandedView ? 'normal' : 'expanded'}View`)}
          handleClick={handleExpandedView}
        />
      </div>
      <Stepper className={classes.stepper} nonLinear={nonLinear} orientation={orientation}>
        {displayStatuses?.map((step, index) => (
          <MuiStep
            key={`wf-${step.statusCode}-${index}`}
            expanded
            active={step.current}
            completed={step.completed}
            index={getIndexByVersion(step, displayStatuses)}
          >
            {!collapsed && checkVersions(step) && (
              <h3 className={styles.LabelDivider}>
                <span className={styles.VersionLabel}>{t('dialog.statusVersion')} {getStatusVersion(step)}</span>
              </h3>
            )}
            <StepLabel>
              <span className={styles.Label}>
                {!collapsed && (!isPlantValidation(step.statusCode) || isSubCR)
                  && (getCRorSubCRLabelStatus(step.statusCode))}
                {!collapsed && isPlantValidation(step.statusCode) && !isSubCR
                  && t('workflow.plantDecision', getCompletedTotalPlantsByLabel(step?.ipvData.plantSumLabel))}
                {collapsed
                  && step.labelCollapsed}
              </span>
            </StepLabel>
            {!collapsed && (step.current || step.completed) && (!isPlantValidation(step.statusCode) || isSubCR)
              && (
                <StepContent>
                  {(isSubCR && isPlantValidation(step.statusCode))
                    ? (
                      <SubCRMiniWF id={id} collapsed={collapsed} directStatuses={step?.subStatuses} />
                    )
                    : (
                      <>
                        <ul className={styles.User}><li>{step?.actionTakenBy?.summary}</li></ul>
                        <ul>
                          {(step?.actionDate !== '' && step?.actionTaken !== '') && (
                            <TooltipComponent
                              key={`tooltip-${step.statusCode}-${index}`}
                              id={`tooltip-${index}`}
                              title={`${checkVersionChip(step.version)} ${step?.actionDate} - ${getActionTakenLabel(step?.actionTaken, step.statusCode)}`}
                            >
                              <li>
                                <UniversalChip
                                  chipType={chipTypes.WITH_FUNCTION}
                                  key={`wf-${step.statusCode}-${index}`}
                                  id={`wf-${index}`}
                                  icon={<i className={getStautsIconClassName(step)} />}
                                  text={`${checkVersionChip(step.version)} ${step?.actionDate} ${getActionTakenLabel(step?.actionTaken, step.statusCode)}`}
                                  onClick={() => stepHandleClick(step)}
                                  active={stepStatus?.statusCode === step?.statusCode}
                                  noHighlightChip
                                />
                              </li>
                            </TooltipComponent>
                          )}
                        </ul>
                      </>
                    )}
                </StepContent>
              )}
            {!collapsed && (step.current || step.completed) && isPlantValidation(step.statusCode) && !isSubCR
              && (
                <StepContent>
                  <ul className={styles.UlPlantSummary}>
                    {Object.entries(step?.ipvData.plantCounter ?? {})?.filter(([, qty]) => qty > 0)?.map(([status, qty]) => (
                      <li key={`${status}-${qty}`}>{`${String(qty).padStart(2, '0')} - ${t(`workflow.${status}`)}`}</li>
                    ))}
                  </ul>
                  <ul>
                    {step?.ipvData.plantStatuses?.map((plant, indexPlant) => (
                      <TooltipComponent
                        key={`tooltip-plant--${plant?.wkzNumber}`}
                        id={`tooltip-plant-${indexPlant}`}
                        title={generatePlantName(plant)}
                      >
                        <li className={styles.LiChipPlant}>
                          <UniversalChip
                            subCrStausColor={checkSubCrStatus(plant)}
                            chipType={chipTypes.WITH_FUNCTION}
                            icon={getStatusIconForPlant(plant)}
                            text={generatePlantName(plant)}
                            active={plantSelected?.wkzNumber === plant?.wkzNumber}
                            onClick={() => handlePlantClick(plant.subCampaignId, plant)}
                            noHighlightChip
                            dot={plant?.requestedForChange ? 'Alert' : ''}
                          />
                        </li>
                      </TooltipComponent>
                    ))}
                  </ul>
                </StepContent>
              )}
          </MuiStep>
        ))}
      </Stepper>
      {open && (
        <DialogStatus
          title={getCRorSubCRLabelStatus(stepStatus?.statusCode)}
          close={closeDialogStatus}
          isVANCR={isVANCR}
          data={stepStatus || workflow?.statusUserHasToSee}
          headerLanguage={headerLanguage}
          getActionTakenLabel={getActionTakenLabel}
        />
      )}

      {plantSelected && (
        <DialogPlantWorkflow
          plant={plantSelected}
          subCRId={subCRClicked}
          close={closePlantDialog}
          headerLanguage={headerLanguage}
          isVANCR={isVANCR}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  isVANCR: getIsVANCR(state),
});

export default connect(mapStateToProps)(StepsComponent);
